import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { BillingPeriod } from "@/lib/billing";

/**
 * 課金ダッシュボードの絞り込み状態。BillingPage は他ビューへ移るとアンマウントされるため、
 * 選択をコンポーネントの外に置いて画面を行き来しても保つ。
 * 再読み込み後の復元とリンク共有は `BillingFilterUrlSync` が URL クエリで担うので、
 * ここでは永続化しない。唯一の例外は includeArchived で、URL を持たない初回訪問でも
 * 好みを保てるよう localStorage に残す。
 */
interface BillingFilterState {
	period: BillingPeriod;
	/** 空配列は「全カテゴリ」を意味する。 */
	categoryIds: string[];
	/** 超管理者が選んだ組織。空文字は「自組織」を意味し、超管理者以外は常に空のまま。 */
	organizationId: string;
	/** 選択中組織の表示名。組織一覧の再取得を待たずにラベルを出せるよう ID と一緒に持つ。 */
	organizationName: string;
	/** アーカイブ済みプロジェクトのカードを一覧に含めるか。閲覧者の好みなので永続化する。 */
	includeArchived: boolean;
	setPeriod: (period: BillingPeriod) => void;
	setCategoryIds: (categoryIds: string[]) => void;
	setOrganization: (organizationId: string, organizationName: string) => void;
	setIncludeArchived: (includeArchived: boolean) => void;
}

export const useBillingFilterStore = create<BillingFilterState>()(
	persist(
		(set) => ({
			period: "allTime",
			categoryIds: [],
			organizationId: "",
			organizationName: "",
			includeArchived: true,
			setPeriod: (period) => set({ period }),
			setCategoryIds: (categoryIds) => set({ categoryIds }),
			setOrganization: (organizationId, organizationName) =>
				set({ organizationId, organizationName }),
			setIncludeArchived: (includeArchived) => set({ includeArchived }),
		}),
		{
			name: "izanagi-billing-filter",
			partialize: (state) => ({ includeArchived: state.includeArchived }),
		},
	),
);
